import { fetchAuthHash } from "@/api/v4/churnkey";
import { UserType } from "@/types";

export const loadChurnkey = (appId: string): HTMLElement | null => {
  if (window.churnkey && window.churnkey.created) {
    return null
  }

  const churnkeyTag = document.createElement('script');
  churnkeyTag.setAttribute('src', 'https://assets.churnkey.co/js/app.js?appId=' + appId)
  churnkeyTag.setAttribute('async', 'true')
  window.churnkey = { created: true }

  return churnkeyTag
}

interface ChurnkeyCheckConfig {
  appId: string
  mode: string
  subscriptionId?: string
}

/**
 * Shows a pop-up if this user's most recent payment failed.
 *
 * @link https://docs.churnkey.co/failed-payment-wall
 * @param churnkeyConfig - Churnkey Check configuration options
 */
export const failedPaymentWall = async (user: any, churnkeyConfig: ChurnkeyCheckConfig) => {
  if (!user.stripeId || user.plan === UserType.FREE || !window.churnkey || !window.churnkey.created || !window.churnkey.check) {
    return
  }

  const { data, error } = await fetchAuthHash()

  if (error) {
    return console.error('[churnkey] Failed to fetch user_hash');
  }

  const { userHash } = data;

  window.churnkey.check('failed-payment', {
    ...churnkeyConfig,
    customerId: user.stripeId,
    authHash: userHash, // required
    provider: 'stripe',
    softWall: true, // set to true to allow exiting wall
    forceCheck: false, // recommended to leave this to false to avoid redundant checks
    gracePeriodDays: 0, // allow a grace period in which failed payment wall is shown but can be exited (soft wall)
    ignoreInvoicesWithoutAttempt: true, // set to true to ignore invoices without a failed charge
  })
}

interface ChurnkeyInitConfig {
  appId: string
  mode: string
  subscriptionId: string
  authHash: string
  handleCancel?: (customer: any, surveyAnswer: string) => Promise<any>
  handlePause?: (customer: any, pauseDuration: any) => Promise<any>,
  onGoToAccount?: (sessionResults?: any) => void,
  onDiscount?: (customer: any, coupon: any) => void,
  onCancel?: (customer: any, surveyResponse: string) => void,
}

/**
 * Start the Churnkey's cancel flow.
 *
 * @link https://docs.churnkey.co/further-configuration
 * @param churnkeyConfig - Churnkey Init configuration options
 */
export const churnkeyCancelFlow = async (user: any, churnkeyConfig: ChurnkeyInitConfig) => {
  if (!user.stripeId || user.plan === UserType.FREE) {
    throw new Error('User is not in a paid plan')
  }

  if (!window.churnkey || !window.churnkey.created || !window.churnkey.init) {
    throw new Error('Churnkey is not initialized')
  }

  window.churnkey.init('restart', {
    ...churnkeyConfig,
    customerId: user.stripeId,
    provider: 'stripe',
  })
}
